import type { TFunction } from 'i18next';

import type {
  AuditEntry,
  AuditRelated,
} from '@/components/shared/audit-trail-panel';
import { formatDate, formatMoney } from '@/utils/format';

import { buildAuditContext, formatAuditActor, isContextOnlyAction } from './audit-context';

/** Flat action codes written by the server (also used as i18n key suffixes under `actions.`). */
export const FINANCIAL_AUDIT_ACTIONS = [
  'fund.create',
  'fund.import',
  'fund.update',
  'fund.balance_adjust',
  'fund.custom_type.create',
  'fund.custom_type.update',
  'period.create',
  'period.close',
  'period.reopen',
  'contribution.record',
  'contribution.update',
  'contribution.void',
  'batch.create',
  'batch.balance',
  'batch.post',
  'pledge.create',
  'pledge.cancel',
  'project.create',
  'project.activate',
  'project.close',
  'project.reopen',
  'giving.identity.view',
  'expense.create',
  'expense.approve',
  'expense.void',
  'expense_category.create',
  'external_party.create',
  'external_party.update',
  'welfare.create',
  'welfare.update',
  'welfare.review',
  'welfare.approve',
  'welfare.reject',
  'welfare.disburse',
  'welfare.void',
  'welfare.read',
  'budget.create',
  'budget.update',
  'budget.activate',
  'budget.close',
  'financial_position.create',
  'financial_position.update',
  'financial_position.balance_adjust',
  'financial_position.transaction_record',
  'financial_position.transaction_void',
  'payment_connection.save',
  'payment_connection.test',
  'payment_connection.disable',
  'payment_connection.enable',
  'payment_intent.reconcile.record',
  'payment_intent.reconcile.reject',
  'audit.export',
] as const;

export const FINANCIAL_AUDIT_ENTITY_TYPES = [
  'fund',
  'fund_custom_type',
  'period',
  'contribution',
  'batch',
  'pledge',
  'project',
  'expense',
  'expense_category',
  'external_party',
  'welfare',
  'budget',
  'financial_position_item',
  'financial_position_transaction',
  'payment_connection',
  'payment_intent',
] as const;

const MONEY_FIELDS = new Set([
  'amountMinor',
  'totalAmountMinor',
  'expectedTotalMinor',
  'actualTotalMinor',
  'targetAmountMinor',
  'requestedAmountMinor',
  'approvedAmountMinor',
  'plannedAmountMinor',
  'balanceMinor',
  'newBalanceMinor',
  'previousBalanceMinor',
  'recordedAmountMinor',
  'confirmedAmountMinor',
]);

const DATE_FIELDS = new Set([
  'contributionDate',
  'expenseDate',
  'startDate',
  'endDate',
  'periodStart',
  'periodEnd',
  'balanceAsOfDate',
  'dateOfBirth',
  'trialEndsAt',
  'currentPeriodEnd',
  'sessionDate',
]);

/** Internal identifiers — never shown when a name sibling exists, and skipped when they look like ids. */
const HIDDEN_FIELDS = new Set([
  'fundId',
  'memberId',
  'periodId',
  'batchId',
  'pledgeId',
  'projectId',
  'categoryId',
  'externalPartyId',
  'occurrenceId',
  'giverIdentityRef',
  'paymentIntentId',
  'connectionId',
  'entityId',
  'isVoided',
  'isTest',
  'isActive',
  'customTypeId',
  'sortOrder',
]);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const UUID_IN_TEXT_RE =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/** Remove raw identifiers from stored audit summaries (legacy rows). */
export const stripIdsFromAuditText = (text: string): string =>
  text
    .replace(UUID_IN_TEXT_RE, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .replace(/\bfor church\s*$/i, '')
    .trim();

export const resolveAuditActionLabel = (
  action: string,
  storedSummary: string,
  t: TFunction,
): string => {
  const key = `actions.${action}`;
  const translated = t(key);
  if (translated !== key) return translated;
  const sanitized = stripIdsFromAuditText(storedSummary);
  if (sanitized) return sanitized;
  return action
    .split('.')
    .map((part) => part.replace(/_/g, ' '))
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' — ');
};

export interface AuditLogRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  summary: string;
  actorUserId: string | null;
  actorRole: string | null;
  actorName: string | null;
  severity: 'info' | 'notable' | 'critical';
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

const metaString = (metadata: Record<string, unknown> | null, key: string): string | null => {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

const metaNumber = (metadata: Record<string, unknown> | null, key: string): number | null => {
  const value = metadata?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const resolveCurrency = (
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
  metadata: Record<string, unknown> | null,
  defaultCurrency: string | null = null,
): string | null => {
  for (const source of [after, before, metadata]) {
    if (typeof source?.currency === 'string' && source.currency) return source.currency;
  }
  return defaultCurrency;
};

const formatScalar = (
  key: string,
  value: unknown,
  options: {
    currency: string | null;
    locale: string;
    emptyLabel: string;
    yesLabel: string;
    noLabel: string;
    giftTypeLabel: (code: string) => string;
    statusLabel: (code: string) => string;
    positionKindLabel: (code: string) => string;
    planLabel: (code: string) => string;
  },
): string => {
  if (value === undefined || value === null || value === '') return options.emptyLabel;
  if (typeof value === 'boolean') return value ? options.yesLabel : options.noLabel;
  if (MONEY_FIELDS.has(key) && typeof value === 'number' && options.currency) {
    return formatMoney(value, options.currency, { locale: options.locale });
  }
  if (DATE_FIELDS.has(key) && typeof value === 'string') {
    return formatDate(value, { locale: options.locale, style: 'medium' });
  }
  if (key === 'status' && typeof value === 'string') {
    return options.statusLabel(value);
  }
  if (key === 'kind' && typeof value === 'string') {
    return options.positionKindLabel(value);
  }
  if (key === 'contributionType' && typeof value === 'string') {
    return options.giftTypeLabel(value);
  }
  if (key === 'plan' && typeof value === 'string') {
    return options.planLabel(value);
  }
  if (
    (key === 'requestType' || key === 'memberType' || key === 'platform' || key === 'relationship') &&
    typeof value === 'string'
  ) {
    return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
  if (typeof value === 'string' && UUID_RE.test(value)) {
    return options.emptyLabel;
  }
  if (typeof value === 'object') return options.emptyLabel;
  return String(value);
};

export const formatAuditChanges = (
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
  options: {
    locale: string;
    emptyLabel: string;
    yesLabel: string;
    noLabel: string;
    fieldLabel: (field: string) => string;
    giftTypeLabel: (code: string) => string;
    statusLabel: (code: string) => string;
    positionKindLabel: (code: string) => string;
    planLabel: (code: string) => string;
    defaultCurrency?: string | null;
    metadata?: Record<string, unknown> | null;
  },
): AuditEntry['changes'] => {
  if (!before && !after) return undefined;

  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  const currency = resolveCurrency(
    before,
    after,
    options.metadata ?? null,
    options.defaultCurrency ?? null,
  );
  const changes: NonNullable<AuditEntry['changes']> = [];

  for (const key of keys) {
    if (HIDDEN_FIELDS.has(key)) continue;
    if (key === 'currency' && [...keys].some((k) => MONEY_FIELDS.has(k))) continue;
    if (key.endsWith('Id') || key.endsWith('Ref')) continue;

    const prev = before?.[key];
    const next = after?.[key];
    if (JSON.stringify(prev) === JSON.stringify(next)) continue;

    // Prefer names; if both sides are raw ids, skip the row entirely.
    if (
      (typeof prev === 'string' && UUID_RE.test(prev) && prev === next) ||
      (typeof prev === 'string' && UUID_RE.test(prev) && typeof next === 'string' && UUID_RE.test(next))
    ) {
      continue;
    }

    const formattedBefore = formatScalar(key, prev, {
      currency,
      locale: options.locale,
      emptyLabel: options.emptyLabel,
      yesLabel: options.yesLabel,
      noLabel: options.noLabel,
      giftTypeLabel: options.giftTypeLabel,
      statusLabel: options.statusLabel,
      positionKindLabel: options.positionKindLabel,
      planLabel: options.planLabel,
    });
    const formattedAfter = formatScalar(key, next, {
      currency,
      locale: options.locale,
      emptyLabel: options.emptyLabel,
      yesLabel: options.yesLabel,
      noLabel: options.noLabel,
      giftTypeLabel: options.giftTypeLabel,
      statusLabel: options.statusLabel,
      positionKindLabel: options.positionKindLabel,
      planLabel: options.planLabel,
    });

    if (formattedBefore === options.emptyLabel && formattedAfter === options.emptyLabel) continue;

    changes.push({
      field: options.fieldLabel(key),
      before: formattedBefore,
      after: formattedAfter,
    });
  }

  return changes.length > 0 ? changes : undefined;
};

const relatedRecord = (
  entityType: string,
  entityId: string | null,
  metadata: Record<string, unknown> | null,
  t: TFunction,
): AuditRelated | undefined => {
  const fundId =
    typeof metadata?.fundId === 'string'
      ? metadata.fundId
      : entityType === 'fund' && entityId
        ? entityId
        : null;
  const receiptNumber = metaNumber(metadata, 'receiptNumber');
  const entityName = metaString(metadata, 'name') || metaString(metadata, 'fundName');

  if (entityType === 'contribution' && entityId) {
    return {
      kind: 'contribution',
      id: entityId,
      label:
        receiptNumber !== null
          ? t('links.viewReceipt', { number: receiptNumber })
          : t('links.viewContribution'),
    };
  }
  if (entityType === 'fund' && (entityId || fundId)) {
    return {
      kind: 'fund',
      id: (entityId ?? fundId) as string,
      label: entityName ? t('links.viewNamedFund', { name: entityName }) : t('links.viewFund'),
    };
  }
  if (entityType === 'expense' && fundId) {
    return {
      kind: 'fund',
      id: fundId,
      label: entityName ? t('links.viewNamedFund', { name: entityName }) : t('links.viewFund'),
    };
  }
  if (entityType === 'period') {
    return {
      kind: 'href',
      href: '/giving/periods',
      label: entityName ? t('links.viewNamedPeriod', { name: entityName }) : t('links.viewPeriods'),
    };
  }
  if (entityType === 'budget' && entityId) {
    return {
      kind: 'href',
      href: `/budgets/${entityId}`,
      label: entityName ? t('links.viewNamedBudget', { name: entityName }) : t('links.viewBudgets'),
    };
  }
  if (entityType === 'welfare') {
    return {
      kind: 'href',
      href: entityId ? `/welfare?request=${entityId}` : '/welfare',
      label: t('links.viewWelfare'),
    };
  }
  if (entityType === 'project' || entityType === 'pledge') {
    return {
      kind: 'href',
      href: '/projects',
      label: entityName ?? t('links.viewPledges'),
    };
  }
  if (entityType === 'payment_connection') {
    return { kind: 'href', href: '/payments', label: t('links.viewPayments') };
  }
  return undefined;
};

type FormatOptions = {
  locale: string;
  emptyLabel: string;
  yesLabel: string;
  noLabel: string;
  fieldLabel: (field: string) => string;
  giftTypeLabel: (code: string) => string;
  statusLabel: (code: string) => string;
  positionKindLabel: (code: string) => string;
  planLabel: (code: string) => string;
  defaultCurrency?: string | null;
  metadata?: Record<string, unknown> | null;
};

const pushFact = (
  changes: NonNullable<AuditEntry['changes']>,
  field: string,
  after: string | null,
  before: string | null = null,
) => {
  if (!after && !before) return;
  changes.push({ field, before, after });
};

const factsFromSources = (
  sources: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
  options: FormatOptions,
): NonNullable<AuditEntry['changes']> => {
  const merged: Record<string, unknown> = {};
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      if (merged[key] !== undefined && merged[key] !== null && merged[key] !== '') continue;
      if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
        merged[key] = source[key];
      }
    }
  }
  return formatCreateDetails(merged, options) ?? [];
};

const buildContributionVoidChanges = (
  row: AuditLogRow,
  options: FormatOptions,
): AuditEntry['changes'] => {
  const changes: NonNullable<AuditEntry['changes']> = [];
  pushFact(
    changes,
    options.fieldLabel('status'),
    options.statusLabel('voided'),
    options.statusLabel('active'),
  );
  changes.push(
    ...factsFromSources(
      [row.metadata, row.afterValue],
      ['amountMinor', 'currency', 'fundName', 'contributionDate', 'contributionType'],
      options,
    ),
  );
  const reason = metaString(row.metadata, 'reason');
  if (reason) {
    pushFact(changes, options.fieldLabel('reason'), reason);
  }
  return changes.length > 0 ? changes : undefined;
};

const buildExpenseLifecycleChanges = (
  row: AuditLogRow,
  nextStatus: 'approved' | 'voided',
  options: FormatOptions,
): AuditEntry['changes'] => {
  const changes: NonNullable<AuditEntry['changes']> = [];
  const previousStatus =
    typeof row.beforeValue?.status === 'string' ? row.beforeValue.status : null;
  pushFact(
    changes,
    options.fieldLabel('status'),
    options.statusLabel(nextStatus),
    previousStatus ? options.statusLabel(previousStatus) : options.statusLabel('pending_approval'),
  );
  changes.push(
    ...factsFromSources(
      [row.metadata, row.afterValue],
      ['amountMinor', 'currency', 'fundName', 'payee', 'expenseDate'],
      options,
    ),
  );
  const reason = metaString(row.metadata, 'reason');
  if (reason) {
    pushFact(changes, options.fieldLabel('reason'), reason);
  }
  return changes.length > 0 ? changes : undefined;
};

const buildPeriodLifecycleChanges = (
  row: AuditLogRow,
  nextStatus: 'closed' | 'open',
  options: FormatOptions,
): AuditEntry['changes'] => {
  const changes: NonNullable<AuditEntry['changes']> = [];
  const previousStatus = nextStatus === 'closed' ? 'open' : 'closed';
  pushFact(
    changes,
    options.fieldLabel('status'),
    options.statusLabel(nextStatus),
    options.statusLabel(previousStatus),
  );
  const name = metaString(row.metadata, 'name');
  if (name) {
    pushFact(changes, options.fieldLabel('name'), name);
  }
  const reason = metaString(row.metadata, 'reason');
  if (reason) {
    pushFact(changes, options.fieldLabel('reason'), reason);
  }
  return changes.length > 0 ? changes : undefined;
};

export const presentAuditEntry = (
  row: AuditLogRow,
  options: {
    t: TFunction;
    tGiving: TFunction;
    locale: string;
    defaultCurrency?: string | null;
  },
): AuditEntry => {
  const { t, tGiving, locale, defaultCurrency = null } = options;
  const emptyLabel = t('emptyValue');
  const yesLabel = t('values.yes');
  const noLabel = t('values.no');
  const giftTypeLabel = (code: string) =>
    tGiving(`kinds.${code}`, { defaultValue: code });
  const statusLabel = (code: string) => t(`values.${code}`, { defaultValue: code });
  const positionKindLabel = (code: string) =>
    t(`values.positionKind.${code}`, { defaultValue: code });
  const planLabel = (code: string) => t(`values.plans.${code}`, { defaultValue: code });
  const fieldLabel = (field: string) => t(`fields.${field}`, { defaultValue: field });
  const formatOptions: FormatOptions = {
    locale,
    emptyLabel,
    yesLabel,
    noLabel,
    fieldLabel,
    giftTypeLabel,
    statusLabel,
    positionKindLabel,
    planLabel,
    defaultCurrency,
    metadata: row.metadata,
  };

  // Short action title. The line under it names the person or record.
  const summary = resolveAuditActionLabel(row.action, row.summary, t);
  const context = buildAuditContext(row, {
    t,
    locale,
    fieldLabel,
    statusLabel,
  });
  const description = context.description;
  let changes: AuditEntry['changes'];

  if (isContextOnlyAction(row.action)) {
    changes = context.facts.length > 0 ? context.facts : undefined;
  } else if (row.action === 'contribution.void') {
    changes = buildContributionVoidChanges(row, formatOptions);
  } else if (row.action === 'contribution.record') {
    changes = formatCreateDetails(row.afterValue, formatOptions);
  } else if (row.action === 'contribution.update') {
    changes = formatAuditChanges(row.beforeValue, row.afterValue, {
      ...formatOptions,
      metadata: row.metadata,
    });
  } else if (row.action === 'expense.create') {
    changes = formatCreateDetails(row.afterValue, formatOptions);
  } else if (row.action === 'expense.approve') {
    changes = buildExpenseLifecycleChanges(row, 'approved', formatOptions);
  } else if (row.action === 'expense.void') {
    changes = buildExpenseLifecycleChanges(row, 'voided', formatOptions);
  } else if (row.action === 'period.close') {
    changes = buildPeriodLifecycleChanges(row, 'closed', formatOptions);
  } else if (row.action === 'period.reopen') {
    changes = buildPeriodLifecycleChanges(row, 'open', formatOptions);
  } else if (row.action.endsWith('.create') || row.action.endsWith('.record')) {
    changes = formatCreateDetails(row.afterValue, formatOptions);
  } else {
    changes = formatAuditChanges(row.beforeValue, row.afterValue, {
      ...formatOptions,
      metadata: row.metadata,
    });
  }

  if (!isContextOnlyAction(row.action) && context.facts.length > 0) {
    const seen = new Set((changes ?? []).map((change) => change.field));
    const extras = context.facts.filter((fact) => !seen.has(fact.field));
    if (extras.length > 0) changes = [...(changes ?? []), ...extras];
  }

  const related = relatedRecord(row.entityType, row.entityId, row.metadata, t);

  return {
    id: row.id,
    at: row.createdAt,
    summary,
    description,
    actor: formatAuditActor(row.action, row.actorName, t),
    changes,
    related,
  };
};

/** Present create/record payloads as "Fund: Building" rather than "Not set → Building". */
const formatCreateDetails = (
  after: Record<string, unknown> | null,
  options: {
    locale: string;
    emptyLabel: string;
    yesLabel: string;
    noLabel: string;
    fieldLabel: (field: string) => string;
    giftTypeLabel: (code: string) => string;
    statusLabel: (code: string) => string;
    positionKindLabel: (code: string) => string;
    planLabel: (code: string) => string;
    defaultCurrency?: string | null;
    metadata?: Record<string, unknown> | null;
  },
): AuditEntry['changes'] => {
  if (!after) return undefined;
  const currency = resolveCurrency(
    null,
    after,
    options.metadata ?? null,
    options.defaultCurrency ?? null,
  );
  const changes: NonNullable<AuditEntry['changes']> = [];

  for (const [key, value] of Object.entries(after)) {
    if (HIDDEN_FIELDS.has(key) || key.endsWith('Id') || key.endsWith('Ref')) continue;
    if (key === 'currency' && Object.keys(after).some((k) => MONEY_FIELDS.has(k))) continue;
    if (value === undefined || value === null || value === '') continue;

    const formatted = formatScalar(key, value, {
      currency,
      locale: options.locale,
      emptyLabel: options.emptyLabel,
      yesLabel: options.yesLabel,
      noLabel: options.noLabel,
      giftTypeLabel: options.giftTypeLabel,
      statusLabel: options.statusLabel,
      positionKindLabel: options.positionKindLabel,
      planLabel: options.planLabel,
    });
    if (formatted === options.emptyLabel) continue;

    changes.push({
      field: options.fieldLabel(key),
      before: null,
      after: formatted,
    });
  }

  return changes.length > 0 ? changes : undefined;
};
